"use client"

import { useMemo, useState } from "react"
import Image from "next/image"
import { MapPin, Mountain, Route, Clock, CheckCircle2, XCircle, Filter, Search, BarChart3 } from "lucide-react"
import type { TrailStage, TrailStats } from "@/lib/pekoe-trail"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet"
import {
    ChartContainer,
    ChartLegend,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

const chartConfig = {
    distance: {
        label: "Distance (km)",
        color: "hsl(var(--primary))",
    },
    elevation: {
        label: "Elevation gain (m)",
        color: "hsl(var(--secondary))",
    },
}

type PekoeTrailDashboardProps = {
    stages: TrailStage[]
    stats: TrailStats
}

const sortOptions = [
    { value: "stage", label: "Stage order" },
    { value: "distance-desc", label: "Distance: longest" },
    { value: "distance-asc", label: "Distance: shortest" },
    { value: "gain-desc", label: "Elevation gain: highest" },
]

export default function PekoeTrailDashboard({ stages, stats }: PekoeTrailDashboardProps) {
    const [searchQuery, setSearchQuery] = useState("")
    const [difficulty, setDifficulty] = useState("All")
    const [status, setStatus] = useState("All")
    const [tag, setTag] = useState("All")
    const [sortBy, setSortBy] = useState("stage")
    const [minDistance, setMinDistance] = useState("")
    const [maxDistance, setMaxDistance] = useState("")
    const [activeStage, setActiveStage] = useState<TrailStage | null>(null)
    const [isSheetOpen, setIsSheetOpen] = useState(false)
    const [compareSlugs, setCompareSlugs] = useState<string[]>([])

    const tagOptions = useMemo(() => {
        const values = new Set<string>()
        stages.forEach((stage) => stage.tags.forEach((item) => values.add(item)))
        return ["All", ...Array.from(values).sort((a, b) => a.localeCompare(b))]
    }, [stages])

    const difficultyOptions = useMemo(() => {
        const values = new Set<string>()
        stages.forEach((stage) => values.add(stage.technical_data.difficulty))
        return ["All", ...Array.from(values).sort((a, b) => a.localeCompare(b))]
    }, [stages])

    const statusOptions = useMemo(() => {
        const values = new Set<string>()
        stages.forEach((stage) => values.add(stage.status))
        return ["All", ...Array.from(values).sort((a, b) => a.localeCompare(b))]
    }, [stages])

    const filteredStages = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        const min = minDistance ? Number(minDistance) : null
        const max = maxDistance ? Number(maxDistance) : null

        return stages.filter((stage) => {
            if (difficulty !== "All" && stage.technical_data.difficulty !== difficulty) {
                return false
            }
            if (status !== "All" && stage.status !== status) {
                return false
            }
            if (tag !== "All" && !stage.tags.includes(tag)) {
                return false
            }
            if (min !== null && stage.technical_data.distance_km < min) {
                return false
            }
            if (max !== null && stage.technical_data.distance_km > max) {
                return false
            }
            if (!query) {
                return true
            }

            const haystack = [
                stage.title,
                stage.description_short,
                stage.description_full,
                stage.tags.join(" "),
                stage.highlights.join(" "),
                stage.points_of_interest.map((item) => item.title).join(" "),
            ]
                .join(" ")
                .toLowerCase()

            return haystack.includes(query)
        })
    }, [stages, difficulty, status, tag, minDistance, maxDistance, searchQuery])

    const sortedStages = useMemo(() => {
        const list = [...filteredStages]

        switch (sortBy) {
            case "distance-desc":
                return list.sort((a, b) => b.technical_data.distance_km - a.technical_data.distance_km)
            case "distance-asc":
                return list.sort((a, b) => a.technical_data.distance_km - b.technical_data.distance_km)
            case "gain-desc":
                return list.sort((a, b) => b.technical_data.elevation_gain_m - a.technical_data.elevation_gain_m)
            default:
                return list.sort((a, b) => a.stage_number - b.stage_number)
        }
    }, [filteredStages, sortBy])

    const comparisonStages = useMemo(() => {
        if (compareSlugs.length) {
            return compareSlugs
                .map((slug) => stages.find((stage) => stage.slug === slug))
                .filter((stage): stage is TrailStage => Boolean(stage))
        }
        return sortedStages.slice(0, 6)
    }, [compareSlugs, sortedStages, stages])

    const comparisonData = comparisonStages.map((stage) => ({
        name: `S${stage.stage_number}`,
        distance: stage.technical_data.distance_km,
        elevation: stage.technical_data.elevation_gain_m,
    }))

    const handleStageOpen = (stage: TrailStage) => {
        setActiveStage(stage)
        setIsSheetOpen(true)
    }

    const toggleCompare = (stage: TrailStage) => {
        setCompareSlugs((prev) => {
            if (prev.includes(stage.slug)) {
                return prev.filter((slug) => slug !== stage.slug)
            }
            if (prev.length >= 6) {
                return prev
            }
            return [...prev, stage.slug]
        })
    }

    const clearFilters = () => {
        setSearchQuery("")
        setDifficulty("All")
        setStatus("All")
        setTag("All")
        setMinDistance("")
        setMaxDistance("")
        setSortBy("stage")
    }

    const descriptionParagraphs = activeStage
        ? activeStage.description_full
            .split("\n\n")
            .map((paragraph) => paragraph.trim())
            .filter(Boolean)
        : []

    return (
        <div className="bg-background">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(16,185,129,0.25),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(14,116,144,0.2),transparent_40%),linear-gradient(180deg,#f7f6f1,transparent_60%)]" />
                <div className="absolute -top-24 right-0 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
                <div className="absolute bottom-0 left-10 h-52 w-52 rounded-full bg-secondary/20 blur-3xl" />

                <div className="relative container py-16 lg:py-20">
                    <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
                        <div className="max-w-2xl">
                            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Pekoe Trail</p>
                            <h1 className="mt-4 font-[var(--font-display)] text-3xl md:text-5xl lg:text-6xl leading-tight">
                                Trail Intelligence Hub
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-muted-foreground">
                                Explore every stage, compare the climbs, and build your own day-by-day plan for Sri Lanka&apos;s
                                most iconic tea country walk.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs">
                                    Live stage filters
                                </Badge>
                                <Badge variant="outline" className="rounded-full px-3 py-1 text-xs">
                                    Compare elevation + distance
                                </Badge>
                                <Badge variant="outline" className="rounded-full px-3 py-1 text-xs">
                                    Open stage tracking
                                </Badge>
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="rounded-2xl border border-border/60 bg-card/80 p-5 shadow-sm backdrop-blur">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total stages</p>
                                <p className="mt-2 text-3xl font-semibold">{stats.totalStages}</p>
                                <p className="mt-1 text-sm text-muted-foreground">{stats.openStages} currently open</p>
                            </div>
                            <div className="rounded-2xl border border-border/60 bg-card/80 p-5 shadow-sm backdrop-blur">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total distance</p>
                                <p className="mt-2 text-3xl font-semibold">{stats.totalDistanceKm} km</p>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    +{stats.totalElevationGainM.toLocaleString()} m elevation gain
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container py-10">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h2 className="font-[var(--font-display)] text-2xl md:text-3xl">Stage Explorer</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Filter by difficulty, status, or highlight tags and open any stage to see the full narrative.
                        </p>
                    </div>
                    <Button variant="outline" onClick={clearFilters} className="w-fit">
                        Clear filters
                    </Button>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 rounded-2xl border border-border/60 bg-card/70 p-5 backdrop-blur sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Search stages, highlights, tags"
                            className="pl-9"
                            aria-label="Search Pekoe trail stages"
                        />
                    </div>

                    <Select value={difficulty} onValueChange={setDifficulty}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Difficulty" />
                        </SelectTrigger>
                        <SelectContent>
                            {difficultyOptions.map((item) => (
                                <SelectItem key={item} value={item}>
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            {statusOptions.map((item) => (
                                <SelectItem key={item} value={item}>
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* <Select value={tag} onValueChange={setTag}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Tag" />
                        </SelectTrigger>
                        <SelectContent>
                            {tagOptions.map((item) => (
                                <SelectItem key={item} value={item}>
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select> */}

                    <div className="flex items-center gap-2">
                        <Input
                            value={minDistance}
                            onChange={(event) => setMinDistance(event.target.value)}
                            placeholder="Min km"
                            type="number"
                            min={0}
                            className="w-full"
                            aria-label="Minimum distance in kilometers"
                        />
                        <Input
                            value={maxDistance}
                            onChange={(event) => setMaxDistance(event.target.value)}
                            placeholder="Max km"
                            type="number"
                            min={0}
                            className="w-full"
                            aria-label="Maximum distance in kilometers"
                        />
                    </div>

                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Sort" />
                        </SelectTrigger>
                        <SelectContent>
                            {sortOptions.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                    {item.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="mt-10 grid gap-6 lg:grid-cols-[2.2fr_1fr]">
                    <div className="grid gap-6 md:grid-cols-2">
                        {sortedStages.map((stage) => {
                            const duration = stage.technical_data.duration_hours_estimate
                            const isCompared = compareSlugs.includes(stage.slug)
                            return (
                                <div
                                    key={stage.slug}
                                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/70 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                                >
                                    <div className="relative h-40">
                                        {stage.media?.cover_image_url ? (
                                            <Image
                                                src={stage.media.cover_image_url}
                                                alt={stage.title}
                                                fill
                                                sizes="(max-width: 768px) 100vw, 50vw"
                                                className="object-cover"
                                            />
                                        ) : (
                                            <div className="h-full w-full bg-gradient-to-br from-primary/20 via-secondary/20 to-muted" />
                                        )}
                                        <div className="absolute left-4 top-4 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold shadow-sm">
                                            Stage {stage.stage_number}
                                        </div>
                                    </div>

                                    <div className="flex h-full flex-col gap-4 p-5">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="rounded-full text-[11px] uppercase tracking-wide">
                                                {stage.technical_data.difficulty}
                                            </Badge>
                                            {stage.status.toLowerCase() === "open" ? (
                                                <Badge variant="secondary" className="rounded-full text-[11px] uppercase tracking-wide">
                                                    Open
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="rounded-full text-[11px] uppercase tracking-wide">
                                                    {stage.status}
                                                </Badge>
                                            )}
                                        </div>

                                        <div>
                                            <h3 className="font-[var(--font-display)] text-xl leading-tight">{stage.title}</h3>
                                            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                                {stage.description_short}
                                            </p>
                                        </div>

                                        <div className="grid min-w-0 gap-2 text-sm text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                <Route className="h-4 w-4" />
                                                {stage.technical_data.distance_km} km • {stage.technical_data.trail_type}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Mountain className="h-4 w-4" />
                                                +{stage.technical_data.elevation_gain_m} m / -{stage.technical_data.elevation_loss_m} m
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                                                <span className="min-w-0 break-words">
                                                    {stage.locations.start.name} → {stage.locations.end.name}
                                                </span>
                                            </div>
                                            {duration ? (
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    {duration} hrs estimate
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {stage.tags.slice(0, 3).map((tagItem) => (
                                                <span key={tagItem} className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                                                    {tagItem}
                                                </span>
                                            ))}
                                            {stage.tags.length > 3 ? (
                                                <span className="text-xs text-muted-foreground">+{stage.tags.length - 3} more</span>
                                            ) : null}
                                        </div>

                                        <div className="mt-auto flex items-center justify-between">
                                            <Button variant="outline" onClick={() => handleStageOpen(stage)}>
                                                View stage
                                            </Button>
                                            <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Checkbox
                                                    checked={isCompared}
                                                    onCheckedChange={() => toggleCompare(stage)}
                                                    aria-label={`Compare ${stage.title}`}
                                                />
                                                Compare
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="space-y-6">
                        <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Stage count</p>
                                    <p className="mt-2 text-3xl font-semibold">{sortedStages.length}</p>
                                    <p className="text-sm text-muted-foreground">Stages match your filters</p>
                                </div>
                                <div className="rounded-full bg-primary/10 p-3">
                                    <Filter className="h-5 w-5 text-primary" />
                                </div>
                            </div>
                            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                    {stats.openStages} open stages across the trail
                                </div>
                                <div className="flex items-center gap-2">
                                    <XCircle className="h-4 w-4 text-muted-foreground" />
                                    Add remaining stages anytime to extend this dashboard
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="rounded-full bg-secondary/10 p-3">
                                    <BarChart3 className="h-5 w-5 text-secondary" />
                                </div>
                                <div>
                                    <h3 className="font-[var(--font-display)] text-lg">Stage comparison</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {compareSlugs.length
                                            ? "Comparing your selected stages"
                                            : "Showing the first six filtered stages"}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 overflow-x-auto">
                                <div className="min-w-[320px]">
                                    <ChartContainer config={chartConfig} className="h-[260px] w-full">
                                        <BarChart data={comparisonData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                            <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                            <YAxis
                                                yAxisId="distance"
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(value: number) => `${value} km`}
                                            />
                                            <YAxis
                                                yAxisId="elevation"
                                                orientation="right"
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(value: number) => `${value} m`}
                                            />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            <ChartLegend />
                                            <Bar
                                                yAxisId="distance"
                                                dataKey="distance"
                                                fill="var(--color-distance)"
                                                radius={[6, 6, 0, 0]}
                                            />
                                            <Bar
                                                yAxisId="elevation"
                                                dataKey="elevation"
                                                fill="var(--color-elevation)"
                                                radius={[6, 6, 0, 0]}
                                            />
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                                {compareSlugs.length >= 6 ? (
                                    <p className="mt-3 text-xs text-muted-foreground">Maximum of 6 stages selected for comparison.</p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetContent side="right" className="h-[100svh] w-full max-w-none overflow-hidden p-4 sm:h-auto sm:max-w-2xl sm:p-6">
                    {activeStage ? (
                        <div className="max-h-[calc(100svh-4.5rem)] space-y-6 overflow-y-auto pr-2">
                            <SheetHeader>
                                <SheetTitle className="font-[var(--font-display)] text-2xl">
                                    Stage {activeStage.stage_number}: {activeStage.title}
                                </SheetTitle>
                                <SheetDescription>{activeStage.description_short}</SheetDescription>
                            </SheetHeader>

                            <div className="relative h-48 overflow-hidden rounded-xl">
                                {activeStage.media?.cover_image_url ? (
                                    <Image
                                        src={activeStage.media.cover_image_url}
                                        alt={activeStage.title}
                                        fill
                                        sizes="(max-width: 768px) 100vw, 50vw"
                                        className="object-cover"
                                    />
                                ) : (
                                    <div className="h-full w-full bg-gradient-to-br from-primary/20 via-secondary/20 to-muted" />
                                )}
                            </div>

                            <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/40 p-4 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Distance</span>
                                    <span>{activeStage.technical_data.distance_km} km</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Elevation gain</span>
                                    <span>{activeStage.technical_data.elevation_gain_m} m</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Elevation loss</span>
                                    <span>{activeStage.technical_data.elevation_loss_m} m</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Difficulty</span>
                                    <span>{activeStage.technical_data.difficulty}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Trail type</span>
                                    <span>{activeStage.technical_data.trail_type}</span>
                                </div>
                                {activeStage.technical_data.duration_hours_estimate ? (
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Duration</span>
                                        <span>{activeStage.technical_data.duration_hours_estimate} hours</span>
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <h4 className="font-[var(--font-display)] text-lg">Full story</h4>
                                <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                                    {descriptionParagraphs.map((paragraph, index) => (
                                        <p key={`${activeStage.slug}-desc-${index}`} className="leading-relaxed">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h4 className="font-[var(--font-display)] text-lg">Highlights</h4>
                                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                                    {activeStage.highlights.map((item) => (
                                        <li key={item} className="flex items-start gap-2">
                                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h4 className="font-[var(--font-display)] text-lg">Waypoints</h4>
                                <div className="mt-3 grid gap-3">
                                    {activeStage.waypoints.slice(0, 6).map((waypoint) => (
                                        <div key={`${waypoint.km}-${waypoint.direction}`} className="rounded-lg border border-border/60 p-3">
                                            <p className="text-xs uppercase tracking-wide text-muted-foreground">{waypoint.km} km</p>
                                            <p className="mt-1 text-sm text-muted-foreground">{waypoint.direction}</p>
                                        </div>
                                    ))}
                                </div>
                                {activeStage.waypoints.length > 6 ? (
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        {activeStage.waypoints.length - 6} more waypoints in the full guide.
                                    </p>
                                ) : null}
                            </div>

                            <div>
                                <h4 className="font-[var(--font-display)] text-lg">Warnings and tips</h4>
                                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                                    {activeStage.warnings_and_tips.map((item) => (
                                        <li key={item} className="flex items-start gap-2">
                                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-secondary" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ) : null}
                </SheetContent>
            </Sheet>
        </div>
    )
}
